import { useEffect, useState } from 'react'
import { Ticket } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { adminMutate, apiGet, usePermissions, useToast } from './primitives.js'
import { Field, TextInput, SwitchRow } from './form.js'
import { useSaveBar } from '../shell/editing.js'
import { useIntegrationCatalog, IntegrationStatusPill } from '../navigation/integration-status.js'
import { CleAccesPanel } from './CleAccesPanel.js'

/**
 * ADMINISTRATION → INTÉGRATIONS → SHOTGUN (15/10/2026).
 *
 * La section Shotgun existait (MiscPages, « IntegrationsPage ») mais n'était
 * plus atteignable depuis le 31/08 : /settings/integrations menait au
 * catalogue, dont la carte Shotgun pointait… le catalogue. Elle retrouve ici
 * une vraie page, avec le panneau « Clé d'accès » commun.
 *
 * Le JETON a quitté le formulaire de synchronisation : il se change par le
 * panneau, qui l'essaie avant de l'enregistrer. Le formulaire garde ce qui
 * n'est pas secret (activation, identifiant organisateur) et sa Save Bar.
 */

interface BackfillState {
  state: 'IDLE' | 'RUNNING' | 'PAUSED_LIVE' | 'DONE' | 'ERROR'
  events_imported: number
  tickets_imported: number
  request_count: number
  http_429_count: number
  failure_count: number
  oldest_event_at: string | null
  newest_event_at: string | null
  last_checkpoint_at: string | null
  completed_at: string | null
  note: string | null
}

interface TicketingConfig {
  enabled: boolean
  organizer_id: string
  token_configured: boolean
  token_updated_at: string | null
  status: string
  last_success_at: string | null
  last_attempted_at: string | null
  last_error: string | null
  backfill?: BackfillState | null
}

const fmtDT = (iso: string | null) => iso === null ? 'jamais' : new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})
const fmtJour = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })

/** Synchronisation : activation et identifiant organisateur (non secrets). */
function SynchronisationShotgun({ onChange }: { onChange: () => void }) {
  const toast = useToast()
  const permissions = usePermissions()
  const peutConfigurer = permissions.permissions.includes('ticketing.configure')
  const [cfg, setCfg] = useState<TicketingConfig | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [organizer, setOrganizer] = useState('')
  const [testing, setTesting] = useState(false)

  const charger = () => {
    apiGet<TicketingConfig>('/api/v1/ticketing/config').then((c) => {
      setCfg(c); setEnabled(c.enabled); setOrganizer(c.organizer_id)
    }).catch(() => setCfg(null))
  }
  useEffect(charger, [])

  const dirty = cfg !== null && peutConfigurer && (enabled !== cfg.enabled || organizer !== cfg.organizer_id)

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer la billetterie',
    invalid: enabled && organizer.trim() === '' ? 'Identifiant organisateur requis pour activer' : null,
    onSave: async () => {
      const r = await adminMutate<TicketingConfig & { error?: string; message?: string }>(
        'PUT', '/api/v1/ticketing/config', { enabled, organizer_id: organizer.trim() })
      if (r.status !== 200) return r.data.message ?? 'Enregistrement impossible — vérifiez les valeurs.'
      setCfg(r.data); setEnabled(r.data.enabled); setOrganizer(r.data.organizer_id)
      // L'identifiant organisateur conditionne le test de la clé : le panneau relit.
      onChange()
      return true
    },
    onCancel: () => { if (cfg) { setEnabled(cfg.enabled); setOrganizer(cfg.organizer_id) } },
    successMessage: 'Billetterie enregistrée.',
  })

  const tester = async () => {
    setTesting(true)
    try {
      const r = await adminMutate<{ ok: boolean; detail: string; error?: string }>('POST', '/api/v1/ticketing/test')
      if (r.status === 200) toast(r.data.ok ? 'ok' : 'error', r.data.ok ? `Lecture réussie avec la clé enregistrée — ${r.data.detail}` : `La lecture a échoué — ${r.data.detail}`)
      else toast('error', 'Test impossible (droits insuffisants ?)')
    } finally { setTesting(false) }
  }

  if (cfg === null) return null
  const bf = cfg.backfill

  return (
    <section className="lc-panel cle-panel" aria-labelledby="shotgun-synchro">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="shotgun-synchro"><Ticket size={16} aria-hidden="true" /> Synchronisation</h2>
        {cfg.enabled && cfg.status === 'OK'
          ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Active · lecture seule</span>
          : cfg.token_configured
            ? <span className="lc-pill lc-pill--warning">{cfg.enabled ? 'À vérifier' : 'Désactivée'}</span>
            : <span className="lc-pill lc-pill--idle">Non configurée</span>}
      </header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">
          Les soirées, ventes et scans arrivent de Shotgun, en lecture seule : Gestion n’écrit rien chez Shotgun.
          L’identifiant organisateur se trouve dans le Smartboard → Settings → Integrations → Shotgun APIs.
        </p>
        <SwitchRow
          checked={enabled}
          onChange={setEnabled}
          title="Synchronisation activée"
          desc="Gestion interroge Shotgun toutes les 30 secondes, en ne lisant que ce qui a changé."
          disabled={!peutConfigurer}
        />
        <Field label="Identifiant organisateur" helper="Le numéro de l’organisateur dans Shotgun.">
          <TextInput value={organizer} onChange={setOrganizer} placeholder="172185" maxLength={20} ariaLabel="Identifiant organisateur Shotgun" />
        </Field>
        <dl className="cle-faits">
          <div><dt>Dernière synchronisation réussie</dt><dd>{fmtDT(cfg.last_success_at)}</dd></div>
          <div><dt>Dernière tentative</dt><dd>{fmtDT(cfg.last_attempted_at)}</dd></div>
          {cfg.last_error ? <div><dt>Dernière erreur</dt><dd>{cfg.last_error}</dd></div> : null}
        </dl>
        {peutConfigurer ? (
          <div className="cle-actions">
            <button type="button" className="lc-btn" onClick={() => { void tester() }} disabled={testing || !cfg.token_configured}>
              {testing ? 'Lecture en cours…' : 'Lire avec la clé enregistrée'}
            </button>
            {bf && bf.state !== 'DONE' ? (
              <button type="button" className="lc-btn" disabled={bf.state === 'RUNNING' || bf.state === 'PAUSED_LIVE'}
                onClick={() => { void adminMutate('POST', '/api/v1/ticketing/backfill').then(() => charger()) }}>
                {bf.state === 'RUNNING' ? 'Import de l’historique en cours…'
                  : bf.state === 'PAUSED_LIVE' ? 'Import en pause (soirée en cours)'
                    : bf.state === 'ERROR' ? 'Reprendre l’import de l’historique'
                      : 'Importer tout l’historique'}
              </button>
            ) : null}
          </div>
        ) : null}
        {bf && bf.state !== 'IDLE' ? (
          <p className="lc-hint">
            <strong>Historique : </strong>
            {bf.state === 'DONE' ? 'import complet terminé'
              : bf.state === 'PAUSED_LIVE' ? 'en pause — une soirée est en cours, elle passe en priorité'
                : bf.state === 'ERROR' ? `en erreur — ${bf.note ?? ''}`
                  : 'import en cours'}
            {' · '}{bf.events_imported} événements · {bf.tickets_imported} billets
            {bf.oldest_event_at ? ` · du ${fmtJour(bf.oldest_event_at)}` : ''}
            {bf.newest_event_at ? ` au ${fmtJour(bf.newest_event_at)}` : ''}
          </p>
        ) : null}
      </div>
    </section>
  )
}

export function ShotgunIntegrationPage() {
  const meta = useShellMeta()
  const catalogue = useIntegrationCatalog()
  const canon = catalogue?.find((i) => i.id === 'shotgun') ?? null
  // Clé de rendu : relire le panneau quand l'identifiant organisateur change.
  const [version, setVersion] = useState(0)
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page cle-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title cle-titre"><Ticket size={22} aria-hidden="true" /> Shotgun</h1>
            <p className="lc-subtitle cle-sous">
              {canon ? <IntegrationStatusPill status={canon.status} label={canon.status_label} /> : null}
              <span>La billetterie des soirées — ventes, scans et historique, en lecture.</span>
            </p>
            {canon?.status_detail ? <p className="lc-hint">{canon.status_detail}</p> : null}
          </div>
        </header>
        <SynchronisationShotgun onChange={() => setVersion((v) => v + 1)} />
        <CleAccesPanel key={version} integration="shotgun" />
      </main>
    </Shell>
  )
}
